import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { buildEdgeUrl } from '@/utils/edgeFunctions';
import { USER_SELECT_FIELDS, USER_SELECT_FIELDS_INNER } from '@/services/userService';
import { toFrontUser } from '@/utils/userMappers';
import { type Community as AppCommunity, User } from '@/types';
import { encodeCommunitySecret } from '@/utils/communitySecret';
import { getErrorStatus } from '@/utils/errors';
import { isRecord, toRecordArray } from '@/utils/typeGuards';

const STORAGE_BUCKET = 'barrio-hub-manager';
const COMMUNITY_LOGO_RELATIVE_PATH = 'private/images/logo.png';

const buildCommunityLogoPath = (communityId: string): string | null => {
  const normalizedId = `${communityId ?? ''}`
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  if (normalizedId.length === 0) {
    return null;
  }
  return `${normalizedId}/${COMMUNITY_LOGO_RELATIVE_PATH}`;
};

const ensureValidCommunityLogoPath = (communityId: string): string => {
  const path = buildCommunityLogoPath(communityId);
  if (!path) {
    throw new Error('El identificador de la comunidad es obligatorio.');
  }
  return path;
};

// Helpers
const toStringArray = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map(item => `${item}`.trim()).filter(Boolean)));
  }
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return Array.from(new Set(parsed.map(item => `${item}`.trim()).filter(Boolean)));
      }
    } catch {
      // Si no es JSON válido, tratamos la cadena como una lista separada por comas
      return Array.from(new Set(value.split(',').map(item => item.trim()).filter(Boolean)));
    }
  }
  return [];
};

interface DbCommunityRow {
  id: string | number | null;
  name: string | null;
  address?: string | null;
  city?: string | null;
  cp?: string | null;
  cif?: string | null;
  blocks?: unknown;
  floors?: unknown;
  letters?: unknown;
  secret?: string | null;
  show_updates?: boolean | null;
  available_sections?: unknown;
}

export type BasicCommunity = {
  id: string;
  name: string;
  address: string;
  city: string;
  cp?: string;
  cif?: string;
  blocks: string[];
  floors: string[];
  letters: string[];
  secret?: string;
  showUpdates: boolean;
  availableSections: string[];
};

// Mapea la fila de BD (snake_case) a una comunidad base consistente para frontend.
function mapDbCommunity(row: DbCommunityRow | null): BasicCommunity | null {
  if (!row || row.id === undefined || row.id === null) return null;
  return {
    id: String(row.id),
    name: row.name ?? '',
    address: row.address ?? '',
    city: row.city ?? '',
    cp: row.cp ?? undefined,
    cif: row.cif ?? undefined,
    blocks: toStringArray(row.blocks),
    floors: toStringArray(row.floors),
    letters: toStringArray(row.letters),
    secret: typeof row.secret === 'string' ? row.secret : undefined,
    showUpdates: row.show_updates === true,
    availableSections: toStringArray(row.available_sections),
  };
}
export type PublicCommunity = BasicCommunity;

const isBasicCommunity = (community: BasicCommunity | null): community is BasicCommunity => Boolean(community);

const compareCommunitiesByName = <T extends { name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });

type EdgeCommunityRow = {
  id: string | number | null;
  name: string | null;
  address?: string | null;
  city?: string | null;
  cp?: string | null;
  cif?: string | null;
  blocks?: unknown;
  floors?: unknown;
  letters?: unknown;
  secret?: string | null;
  show_updates?: boolean | null;
  available_sections?: unknown;
};

async function fetchPublicCommunitiesFromEdge(): Promise<BasicCommunity[]> {
  const result = await supabase.functions.invoke<EdgeCommunityRow[]>(
    'communities-list',
    {
      method: 'GET',
      headers: {
        'cache-control': 'no-store',
      },
    }
  );

  if (result.error) {
    throw new Error(
      isRecord(result.error) && typeof result.error.message === 'string'
        ? result.error.message
        : 'No se pudieron cargar las comunidades públicas.'
    );
  }

  const payload = Array.isArray(result.data) ? result.data : [];

  return payload
    .map(row =>
      mapDbCommunity({
        id: row.id,
        name: row.name,
        address: row.address,
        city: row.city,
        cp: row.cp,
        cif: row.cif,
        blocks: row.blocks,
        floors: row.floors,
        letters: row.letters,
        secret: row.secret,
        show_updates: row.show_updates,
        available_sections: row.available_sections,
      })
    )
    .filter(isBasicCommunity)
    .sort(compareCommunitiesByName);
}

type AuthOptions = {
  requireAuth?: boolean;
};

const ensureSessionIfRequired = async (options?: AuthOptions) => {
  if (options?.requireAuth === false) {
    return;
  }

  await ensureAuthenticatedSession();
};

export async function getAllCommunities(options?: AuthOptions) {
  if (options?.requireAuth === false) {
    try {
      return await fetchPublicCommunitiesFromEdge();
    } catch (error) {
      console.warn('Fallo al obtener comunidades públicas a través de la Edge Function:', error);
      return [];
    }
  }

  await ensureSessionIfRequired(options);
  const { data, error } = await supabase
    .from('communities')
    .select('id, name, address, city, cp, cif, floors, blocks, letters, secret, show_updates, available_sections')
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || [])
    .map(mapDbCommunity)
    .filter(isBasicCommunity)
    .sort(compareCommunitiesByName);
}

export async function getCommunityById(id: string, options?: AuthOptions) {
  await ensureSessionIfRequired(options);
  const { data, error } = await supabase
    .from('communities')
    .select('id, name, address, city, cp, cif, floors, blocks, letters, secret, show_updates, available_sections')
    .eq('id', id)
    .single();
  if (error) throw error;
  return mapDbCommunity(data);
}

export interface CommunityDraft {
  id?: string;
  name: string;
  address?: string;
  city?: string;
  cp?: string;
  cif?: string;
  blocks?: string[];
  floors?: string[];
  letters?: string[];
  secret?: string;
  showUpdates?: boolean;
  availableSections?: string[];
}

export async function createCommunity(community: Omit<CommunityDraft, 'id'>) {
  await ensureAuthenticatedSession();
  const payload: Record<string, unknown> = {
    name: community.name,
    address: community.address ?? null,
    city: community.city ?? null,
  };

  if (community.cp !== undefined) {
    const cp = typeof community.cp === 'string' ? community.cp.trim() : '';
    payload.cp = cp || null;
  }

  if (community.cif !== undefined) {
    const cif = typeof community.cif === 'string' ? community.cif.trim() : '';
    payload.cif = cif || null;
  }

  if (community.showUpdates !== undefined) {
    payload.show_updates = community.showUpdates === true;
  }

  const sanitizeList = (values?: string[]) => {
    if (values === undefined) return undefined;
    const normalized = Array.from(new Set(values.map(value => `${value}`.trim()).filter(Boolean)));
    return normalized.length > 0 ? normalized : [];
  };

  const blocks = sanitizeList(community.blocks);
  const floors = sanitizeList(community.floors);
  const letters = sanitizeList(community.letters);
  const availableSections = sanitizeList(community.availableSections);

  if (blocks !== undefined) payload.blocks = blocks.length > 0 ? blocks : null;
  if (floors !== undefined) payload.floors = floors.length > 0 ? floors : null;
  if (letters !== undefined) payload.letters = letters.length > 0 ? letters : null;
  if (availableSections !== undefined) {
    payload.available_sections = availableSections.length > 0 ? availableSections : null;
  }

  if (community.secret !== undefined) {
    const trimmedSecret = community.secret.trim();
    payload.secret = trimmedSecret ? encodeCommunitySecret(trimmedSecret) : null;
  }

  const { data, error } = await supabase
    .from('communities')
    .insert([payload])
    .select('id, name, address, city, cp, cif, floors, blocks, letters, secret, show_updates, available_sections')
    .single();
  if (error) throw error;
  return mapDbCommunity(data);
}

export async function updateCommunity(
  communityId: string,
  updates: Partial<Omit<CommunityDraft, 'id'>>
) {
  if (!communityId) {
    throw new Error('El identificador de la comunidad es obligatorio.');
  }

  await ensureAuthenticatedSession();

  const payload: Record<string, unknown> = {};

  if (Object.prototype.hasOwnProperty.call(updates, 'name')) {
    const name = typeof updates.name === 'string' ? updates.name.trim() : '';
    if (!name) {
      throw new Error('El nombre de la comunidad es obligatorio.');
    }
    payload.name = name;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'address')) {
    const address = typeof updates.address === 'string' ? updates.address.trim() : '';
    payload.address = address ? address : null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'city')) {
    const city = typeof updates.city === 'string' ? updates.city.trim() : '';
    payload.city = city ? city : null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'cp')) {
    const cp = typeof updates.cp === 'string' ? updates.cp.trim() : '';
    payload.cp = cp ? cp : null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'cif')) {
    const cif = typeof updates.cif === 'string' ? updates.cif.trim() : '';
    payload.cif = cif ? cif : null;
  }

  const sanitizeList = (values?: string[] | null) => {
    if (values === undefined) return undefined;
    const normalized = Array.from(new Set((values ?? []).map(value => `${value}`.trim()).filter(Boolean)));
    return normalized;
  };

  const blocks = sanitizeList(updates.blocks);
  if (blocks !== undefined) {
    payload.blocks = blocks.length > 0 ? blocks : null;
  }

  const floors = sanitizeList(updates.floors);
  if (floors !== undefined) {
    payload.floors = floors.length > 0 ? floors : null;
  }

  const letters = sanitizeList(updates.letters);
  if (letters !== undefined) {
    payload.letters = letters.length > 0 ? letters : null;
  }

  const availableSections = sanitizeList(updates.availableSections);
  if (availableSections !== undefined) {
    payload.available_sections = availableSections.length > 0 ? availableSections : null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'secret')) {
    const secret = typeof updates.secret === 'string' ? updates.secret.trim() : '';
    payload.secret = secret ? encodeCommunitySecret(secret) : null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'showUpdates')) {
    payload.show_updates = updates.showUpdates === true;
  }

  if (Object.keys(payload).length === 0) {
    return getCommunityById(communityId);
  }

  const { data, error } = await supabase
    .from('communities')
    .update(payload)
    .eq('id', communityId)
    .select('id, name, address, city, cp, cif, floors, blocks, letters, secret, show_updates, available_sections')
    .single();

  if (error) throw error;
  return mapDbCommunity(data);
}

// Obtiene todas las comunidades con el array de miembros (users) poblado
type CommunityRelations = Pick<AppCommunity, 'members' | 'commonAreas' | 'incidents' | 'payments' | 'polls'>;

const EMPTY_RELATIONS: CommunityRelations = {
  members: [],
  commonAreas: [],
  incidents: [],
  payments: [],
  polls: [],
};

const buildCommunitiesWithMembers = (
  communities: BasicCommunity[]
): Array<BasicCommunity & CommunityRelations> => {
  return communities.map(c => ({
    ...c,
    members: [...EMPTY_RELATIONS.members],
    commonAreas: [...EMPTY_RELATIONS.commonAreas],
    incidents: [...EMPTY_RELATIONS.incidents],
    payments: [...EMPTY_RELATIONS.payments],
    polls: [...EMPTY_RELATIONS.polls],
  }));
};

export async function getAllCommunitiesWithMembers(
  options?: AuthOptions
): Promise<Array<BasicCommunity & CommunityRelations>> {
  if (options?.requireAuth === false) {
    const communities = await getAllCommunities({ requireAuth: false });
    if (communities.length === 0) return [] as Array<BasicCommunity & CommunityRelations>;
    return buildCommunitiesWithMembers(communities).sort(compareCommunitiesByName);
  }

  try {
    await ensureAuthenticatedSession();
  } catch (error) {
    const fallbackCommunities = await getAllCommunities({ requireAuth: false });
    if (fallbackCommunities.length === 0) return [] as Array<BasicCommunity & CommunityRelations>;
    return buildCommunitiesWithMembers(fallbackCommunities).sort(
      compareCommunitiesByName
    );
  }

  try {
    const communities = await getAllCommunities();
    if (communities.length === 0) {
      const fallbackCommunities = await getAllCommunities({ requireAuth: false });
      if (fallbackCommunities.length === 0) return [] as Array<BasicCommunity & CommunityRelations>;
      return buildCommunitiesWithMembers(fallbackCommunities).sort(compareCommunitiesByName);
    }

    const communityIds = communities.map(c => c.id).filter((id): id is string => Boolean(id));
    if (communityIds.length === 0) {
      return buildCommunitiesWithMembers(communities).sort(compareCommunitiesByName);
    }

    const { data: usersData, error: usersError } = await supabase
      .from('users')
      .select(USER_SELECT_FIELDS_INNER)
      .in('user_roles_by_community.community_id', communityIds);

    let finalUsersData = usersData;
    if (usersError) {
      const fallback = await supabase
        .from('users')
        .select(USER_SELECT_FIELDS)
        .in('user_roles_by_community.community_id', communityIds);
      if (fallback.error) {
        throw fallback.error;
      }
      finalUsersData = fallback.data;
    }

    const grouped: Record<string, User[]> = {};
    const rawUsers = toRecordArray(finalUsersData);
    rawUsers.forEach((userRecord) => {
      const memberships = Array.isArray(userRecord.user_roles_by_community)
        ? (userRecord.user_roles_by_community as Array<Record<string, unknown>>)
        : [];
      memberships.forEach((membership) => {
        const cid = typeof membership.community_id === 'string' ? membership.community_id : null;
        if (!cid || !communityIds.includes(cid)) return;
        if (!grouped[cid]) grouped[cid] = [];
        grouped[cid].push(toFrontUser(userRecord));
      });
    });
    return communities
      .map(c => ({
        ...c,
        members: grouped[c.id] || [],
        commonAreas: [],
        incidents: [],
        payments: [],
        polls: []
      }))
      .sort(compareCommunitiesByName);
  } catch (error) {
    // Fallback a vista pública si falla la consulta autenticada
    const fallbackCommunities = await getAllCommunities({ requireAuth: false });
    if (fallbackCommunities.length === 0) return [] as Array<BasicCommunity & CommunityRelations>;
    return buildCommunitiesWithMembers(fallbackCommunities).sort(
      compareCommunitiesByName
    );
  }
}

export async function updateCommunityHousingConfig(
  communityId: string,
  config: Partial<Pick<CommunityDraft, 'blocks' | 'floors' | 'letters'>>
) {
  await ensureAuthenticatedSession();
  const sanitizeList = (values?: string[]) => {
    if (!values) return undefined;
    const normalized = Array.from(new Set(values.map(value => `${value}`.trim()).filter(Boolean)));
    return normalized.length > 0 ? normalized : [];
  };

  const updates: Record<string, unknown> = {};
  const blocks = sanitizeList(config.blocks);
  const floors = sanitizeList(config.floors);
  const letters = sanitizeList(config.letters);

  if (blocks !== undefined) updates.blocks = blocks.length > 0 ? blocks : null;
  if (floors !== undefined) updates.floors = floors.length > 0 ? floors : null;
  if (letters !== undefined) updates.letters = letters.length > 0 ? letters : null;

  const { data, error } = await supabase
    .from('communities')
    .update(updates)
    .eq('id', communityId)
    .select('id, name, address, city, cp, cif, floors, blocks, letters, secret, available_sections, show_updates')
    .single();

  if (error) throw error;
  return mapDbCommunity(data);
}

const COMMUNITY_MEMBER_SUMMARIES_PATH = 'community-member-summaries';

export type CommunityMemberSummary = {
  communityId: string;
  memberCount: number;
  managerEmail?: string | null;
  managerPhone?: string | null;
};

export type CommunityUserInitial = {
  id: string;
  initials: string;
};

type CommunityMemberSummariesResponse = {
  summaries?: Array<{
    communityId?: string;
    memberCount?: number;
    managerEmail?: string | null;
    managerPhone?: string | null;
  }>;
  userInitials?: Array<{
    id?: string;
    initials?: string;
  }>;
};

const fetchCommunityMemberSummaries = async (payload: {
  communityIds?: string[];
  userIds?: string[];
}): Promise<CommunityMemberSummariesResponse> => {
  await ensureAuthenticatedSession();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('No hay sesión activa para consultar resúmenes de comunidad.');
  }

  const response = await fetch(buildEdgeUrl(COMMUNITY_MEMBER_SUMMARIES_PATH), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload ?? {}),
  });

  if (!response.ok) {
    throw new Error('No se pudieron obtener los resúmenes de comunidad.');
  }

  const responsePayload = await response.json() as unknown;
  return isRecord(responsePayload)
    ? (responsePayload as CommunityMemberSummariesResponse)
    : {};
};

export async function getCommunityMemberSummaries(
  communityIds: string[]
): Promise<CommunityMemberSummary[]> {
  const ids = Array.from(new Set(communityIds.filter(Boolean)));
  if (ids.length === 0) return [];
  const data = await fetchCommunityMemberSummaries({ communityIds: ids });
  return (data.summaries ?? [])
    .map(summary => ({
      communityId: summary.communityId ?? '',
      memberCount: summary.memberCount ?? 0,
      managerEmail: summary.managerEmail ?? null,
      managerPhone: summary.managerPhone ?? null,
    }))
    .filter(summary => summary.communityId);
}

export async function getCommunityUserInitials(
  communityId: string,
  userIds: string[]
): Promise<Record<string, string>> {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  if (!communityId || ids.length === 0) return {};
  const data = await fetchCommunityMemberSummaries({ communityIds: [communityId], userIds: ids });
  return (data.userInitials ?? []).reduce<Record<string, string>>((acc, row) => {
    if (row?.id && row.initials) {
      acc[row.id] = row.initials;
    }
    return acc;
  }, {});
}

export async function getCommunityUserInitialsForCommunities(
  communityIds: string[],
  userIds: string[]
): Promise<Record<string, string>> {
  const uniqueCommunityIds = Array.from(new Set(communityIds.filter(Boolean)));
  const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
  if (uniqueCommunityIds.length === 0 || uniqueUserIds.length === 0) return {};
  const data = await fetchCommunityMemberSummaries({
    communityIds: uniqueCommunityIds,
    userIds: uniqueUserIds,
  });
  return (data.userInitials ?? []).reduce<Record<string, string>>((acc, row) => {
    if (row?.id && row.initials) {
      acc[row.id] = row.initials;
    }
    return acc;
  }, {});
}

export async function getCommunityImageUrl(
  communityId: string,
  expiresInSeconds = 60 * 60
) {
  await ensureAuthenticatedSession();
  const storagePath = buildCommunityLogoPath(communityId);
  if (storagePath === null) {
    return null;
  }
  try {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, expiresInSeconds);

    if (error || !data?.signedUrl) {
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    return null;
  }
}

export async function uploadCommunityLogo(communityId: string, file: File) {
  await ensureAuthenticatedSession();

  if (!(file instanceof Blob)) {
    throw new Error('El archivo seleccionado no es válido.');
  }

  const storagePath = ensureValidCommunityLogoPath(communityId);
  const normalizedFile =
    file instanceof File && file.name === 'logo.png'
      ? file
      : new File([file], 'logo.png', {
          type: file.type || 'application/octet-stream',
        });

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, normalizedFile, {
      upsert: true,
      contentType: normalizedFile.type || 'application/octet-stream',
      cacheControl: '0',
    });

  if (error) {
    const message = error.message || 'No se pudo actualizar el logo de la comunidad.';
    throw new Error(message);
  }

  return storagePath;
}

export async function removeCommunityLogo(communityId: string) {
  await ensureAuthenticatedSession();

  const storagePath = ensureValidCommunityLogoPath(communityId);
  const { error } = await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);

  if (error) {
    const status = getErrorStatus(error);
    if (status !== 404) {
      const message = error.message || 'No se pudo eliminar el logo de la comunidad.';
      throw new Error(message);
    }
  }

  return storagePath;
}

export async function deleteCommunity(communityId: string) {
  const normalizedId = `${communityId ?? ''}`.trim();
  if (!normalizedId) {
    throw new Error('El identificador de la comunidad es obligatorio.');
  }

  await ensureAuthenticatedSession();

  const { error } = await supabase
    .from('communities')
    .delete()
    .eq('id', normalizedId);

  if (error) throw error;

  const storagePath = buildCommunityLogoPath(normalizedId);
  if (storagePath === null) {
    return;
  }
  try {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([storagePath]);

    if (storageError) {
      console.warn('No se pudo eliminar la imagen de la comunidad:', storageError);
    }
  } catch (storageException) {
    console.warn('Error inesperado al eliminar la imagen de la comunidad:', storageException);
  }
}
